import { createContext, useContext, useState, useEffect } from "react";
import { api } from "../api";

const AuthContext = createContext(null);
const TOKEN_KEY = "tabletstalk_token";
const USER_KEY = "tabletstalk_user";

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

function safeParseUser(raw) {
  try {
    const user = JSON.parse(raw);
    if (!user || typeof user !== "object") return null;
    return user;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  };

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);
      if (!savedToken || !savedUser) {
        if (!cancelled) setLoading(false);
        return;
      }
      const parsed = safeParseUser(savedUser);
      if (!parsed) {
        clearSession();
        if (!cancelled) setLoading(false);
        return;
      }
      if (!cancelled) {
        setToken(savedToken);
        setUser(parsed);
      }
      try {
        const me = await api(`/api/auth/me`, { token: savedToken });
        if (!cancelled && me && !me.detail) {
          setUser(me);
          localStorage.setItem(USER_KEY, JSON.stringify(me));
        }
      } catch {
        clearSession();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
  };

  const logout = async () => {
    try {
      if (token) await api("/api/auth/logout", { method: "POST", token });
    } catch {
      /* ignore */
    }
    clearSession();
    if (window.location.pathname !== "/") {
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new CustomEvent("app:navigate", { detail: { to: "/" } }));
    }
  };

  const updateUser = (updates) => {
    setUser((prev) => {
      const updated = { ...(prev || {}), ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAuthenticated: !!token,
        login,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
